import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import HeadingSmall from "@/components/heading-small"
import { ApiKeyCredentialsForm } from "@/components/integrations/api-key-credentials-form"
import {
  type PatCredentials,
  PatCredentialsForm,
} from "@/components/integrations/pat-credentials-form"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { INTEGRATION_REGISTRY } from "@/lib/integration-registry"
import {
  settingsIntegrationConnectPath,
  settingsIntegrationCredentialConnectPath,
  settingsIntegrationDisconnectPath,
  settingsIntegrationsPath,
  settingsMcpConnectPath,
  settingsMcpDisconnectPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Settings", href: settingsIntegrationsPath() },
]

// How a given kind connects. oauth/mcp redirect to a provider; api_key/pat open
// a credentials dialog. The server reports this per kind.
type Source = "oauth" | "mcp" | "api_key" | "pat"
interface IntegrationState {
  connected: boolean
  source: Source
}

// Category → ordered registry kinds (IMP-382 design). Only connectable kinds are
// listed; a kind the server doesn't report is skipped at render. The single
// HubSpot row is hubspot_crm (the account-level CRM connection).
const CATEGORIES: { title: string; description: string; kinds: string[] }[] = [
  {
    title: "CRM",
    description: "Sync leads and contacts with your CRM.",
    kinds: [
      "hubspot_crm",
      "pipedrive",
      "close",
      "copper",
      "keap",
      "freshsales",
    ],
  },
  {
    title: "Email & Marketing",
    description:
      "Set up campaigns and add contacts to your email and marketing lists.",
    kinds: ["mailchimp", "klaviyo", "active_campaign", "brevo"],
  },
  {
    title: "Scheduling",
    description:
      "Allow your assistant to book appointments and check real-time availability.",
    kinds: ["google_calendar", "outlook_calendar", "cal_com", "calendly"],
  },
  {
    title: "Workspace",
    description:
      "Connect platforms your team runs on for sign-in and collaboration.",
    kinds: ["google_workspace", "slack", "notion"],
  },
  {
    title: "Reputation",
    description: "Read and respond to customer reviews.",
    kinds: ["google_business_profile"],
  },
]

// Temporarily hidden from the catalog. Kept in CATEGORIES above so restoring
// one is a single-line delete here; a category whose kinds are all hidden
// renders nothing at all.
const HIDDEN_KINDS = new Set([
  // CRM
  "close",
  "copper",
  "keap",
  "freshsales",
  // Email & Marketing (whole category)
  "mailchimp",
  "klaviyo",
  "active_campaign",
  "brevo",
  // Scheduling
  "google_calendar",
  "outlook_calendar",
  // Workspace
  "notion",
  // Reputation (whole category)
  "google_business_profile",
])

function IntegrationRow({
  kind,
  state,
  onConnect,
  onDisconnect,
}: {
  kind: string
  state: IntegrationState
  onConnect: (kind: string, source: Source) => void
  onDisconnect: (kind: string, source: Source) => void
}) {
  const registry = INTEGRATION_REGISTRY[kind]
  if (!registry) return null

  return (
    <div className="flex items-center gap-4 py-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center">
        {registry.icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{registry.label}</p>
      </div>
      {state.connected ? (
        <Button
          variant="outline"
          size="sm"
          className="border-destructive text-destructive hover:bg-destructive/10 shrink-0"
          onClick={() => onDisconnect(kind, state.source)}
        >
          Disconnect
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => onConnect(kind, state.source)}
        >
          Connect
        </Button>
      )}
    </div>
  )
}

// Credentials dialog for api_key / pat kinds (oauth/mcp redirect instead).
function ConnectDialog({
  dialog,
  onClose,
}: {
  dialog: { kind: string; source: Source } | null
  onClose: () => void
}) {
  // Fresh state per integration via the `key` on the parent's render, so no
  // reset effect is needed.
  const [apiValues, setApiValues] = useState<Record<string, string>>({})
  const [patValues, setPatValues] = useState<PatCredentials>({
    personalAccessToken: "",
  })
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const registry = dialog ? INTEGRATION_REGISTRY[dialog.kind] : null
  if (!dialog || !registry) return null

  const { kind, source } = dialog
  const isPat = source === "pat"

  function submit() {
    setProcessing(true)
    setError(null)
    const payload = isPat
      ? { personal_access_token: patValues.personalAccessToken }
      : apiValues
    router.post(settingsIntegrationCredentialConnectPath(kind), payload, {
      preserveScroll: true,
      onSuccess: onClose,
      onError: () =>
        setError(
          "Unable to verify credentials. Check that all fields are correct and try again.",
        ),
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Connect {registry.label}</DialogTitle>
          <DialogDescription>{registry.description}</DialogDescription>
        </DialogHeader>

        {isPat ? (
          <PatCredentialsForm
            values={patValues}
            onChange={setPatValues}
            idPrefix={`connect-${kind}`}
          />
        ) : (
          <ApiKeyCredentialsForm
            fields={registry.credentials ?? []}
            values={apiValues}
            onChange={setApiValues}
            idPrefix={`connect-${kind}`}
          />
        )}

        {error && <p className="text-destructive text-sm">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={processing}>
            {processing ? "Connecting…" : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function Integrations({
  integrations,
}: {
  integrations: Record<string, IntegrationState>
}) {
  const [dialog, setDialog] = useState<{ kind: string; source: Source } | null>(
    null,
  )

  function connect(kind: string, source: Source) {
    if (source === "oauth") {
      window.location.href = settingsIntegrationConnectPath(kind)
      return
    }
    if (source === "mcp") {
      window.location.href = settingsMcpConnectPath(kind)
      return
    }
    // api_key / pat → credentials dialog
    setDialog({ kind, source })
  }

  function disconnect(kind: string, source: Source) {
    const path =
      source === "mcp"
        ? settingsMcpDisconnectPath(kind)
        : settingsIntegrationDisconnectPath(kind)
    router.delete(path, { preserveScroll: true })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Integrations" />

      <SettingsLayout>
        <div className="space-y-8">
          <HeadingSmall
            title="Integrations"
            description="Connect the tools your assistant works with."
          />

          {CATEGORIES.map((cat) => {
            const kinds = cat.kinds.filter(
              (k) =>
                !HIDDEN_KINDS.has(k) &&
                integrations[k] &&
                INTEGRATION_REGISTRY[k],
            )
            if (kinds.length === 0) return null

            return (
              <section key={cat.title}>
                <div className="mb-1">
                  <h4 className="text-sm font-medium">{cat.title}</h4>
                  <p className="text-muted-foreground text-sm">
                    {cat.description}
                  </p>
                </div>
                <div className="divide-y">
                  {kinds.map((kind) => (
                    <IntegrationRow
                      key={kind}
                      kind={kind}
                      state={integrations[kind]}
                      onConnect={connect}
                      onDisconnect={disconnect}
                    />
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      </SettingsLayout>

      <ConnectDialog
        key={dialog?.kind ?? "none"}
        dialog={dialog}
        onClose={() => setDialog(null)}
      />
    </AppLayout>
  )
}
